import { IconX } from '@tabler/icons-react';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

type Props = {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
};

/**
 * Bottom sheet on phones, centred dialog on tablets. Portalled to <body> so a
 * sticky or positioned ancestor cannot trap it under the page. Opens with the
 * focus on the field marked `data-autofocus` (a form that creates something),
 * otherwise on the dialog itself.
 */
export function Sheet({ title, onClose, children, actions }: Props) {
  const { t } = useTranslation();
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  // Callers pass a new arrow on every render: the effect below must run once,
  // or each re-render would move the focus back.
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('[data-autofocus]');
    (first ?? ref.current)?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, []);

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click closes; Escape is handled above
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled above
    <div
      className="sheet-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={ref}
        tabIndex={-1}
      >
        <div className="sheet__grip" />
        <div className="sheet__head">
          <h2 id={titleId}>{title}</h2>
          {actions}
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <IconX size={22} />
          </button>
        </div>
        <div className="sheet__body">{children}</div>
      </div>
    </div>,
    document.body
  );
}
